if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      timer = null;
      currentVariant = null;
      warningTimeout = null;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get quantityButtons() {
        return {
          plus: this.querySelector('.quantity__button[name="plus"]'),
          minus: this.querySelector('.quantity__button[name="minus"]')
        };
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          console.log('📦 Initial Variant Stock:', this.currentVariant.inventory_quantity);
          this.updateMedia(this.currentVariant);
          this.setQuantityMax(this.currentVariant);
        }

        // Move Add button above stock
        const addButton = this.querySelector('[name="add"]');
        const stock = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (addButton && stock) {
          stock.parentNode.insertBefore(addButton, stock);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        if (this.timer) {
          clearInterval(this.timer);
        }
        if (this.warningTimeout) {
          clearTimeout(this.warningTimeout);
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        // Set initial value to 1
        this.quantityInput.value = '1';
        this.quantityInput.setAttribute('data-previous-value', '1');

        this.setQuantityBoundaries();
        this.setupQuantityButtonListeners();

        // Real-time input validation
        this.quantityInput.addEventListener('input', () => {
          this.validateQuantityInput();
          this.updateQuantityButtonsState();
        });

        // Change event for manual edits
        this.quantityInput.addEventListener('change', () => {
          this.validateQuantityChange();
          this.updateQuantityButtonsState();
        });

        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setupQuantityButtonListeners() {
        const buttons = this.quantityButtons;
        if (!buttons.plus || !buttons.minus) return;

        // Remove any existing event listeners first to prevent duplicates
        buttons.plus.replaceWith(buttons.plus.cloneNode(true));
        buttons.minus.replaceWith(buttons.minus.cloneNode(true));

        // Get fresh references after clone
        const freshPlus = this.querySelector('.quantity__button[name="plus"]');
        const freshMinus = this.querySelector('.quantity__button[name="minus"]');

        // Plus button click handler - SINGLE increment
        freshPlus.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          console.log('➕ Plus button clicked');
          this.handleQuantityIncrement();
        });

        // Minus button click handler - SINGLE decrement
        freshMinus.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          console.log('➖ Minus button clicked');
          this.handleQuantityDecrement();
        });

        // Update buttons state initially
        this.updateQuantityButtonsState();
      }

      handleQuantityIncrement() {
        if (!this.quantityInput) return;
        
        // Get the actual stock value from screen
        const maxQty = this.getActualStockQuantity();
        const currentValue = parseInt(this.quantityInput.value) || 1;
        
        console.log('➕ Incrementing - Current:', currentValue, 'Available Stock:', maxQty);
        
        // Check if stock is available
        if (maxQty <= 0) {
          console.log('❌ Out of stock');
          this.showStockWarning(0, 'out-of-stock');
          return;
        }
        
        // Check if already at max
        if (currentValue >= maxQty) {
          console.log('⚠️ Already at maximum stock');
          this.showStockWarning(maxQty, 'at-limit');
          return;
        }
        
        // Safe to increment by 1 only
        const newValue = currentValue + 1;
        this.quantityInput.value = newValue;
        this.quantityInput.setAttribute('data-previous-value', newValue.toString());
        
        console.log('✅ Incremented to:', newValue);
        
        // Update button states
        this.updateQuantityButtonsState();
        
        // Show warning if reached limit
        if (newValue >= maxQty) {
          this.showStockWarning(maxQty, 'at-limit');
        } else {
          this.hideStockWarning();
        }

        // Dispatch change event
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      handleQuantityDecrement() {
        if (!this.quantityInput) return;
        
        const minQty = parseInt(this.quantityInput.getAttribute('min')) || 1;
        const currentValue = parseInt(this.quantityInput.value) || 1;
        
        console.log('➖ Decrementing - Current:', currentValue, 'Min:', minQty);
        
        // Only decrement if above minimum
        if (currentValue > minQty) {
          const newValue = currentValue - 1;
          this.quantityInput.value = newValue;
          this.quantityInput.setAttribute('data-previous-value', newValue.toString());
          
          console.log('✅ Decremented to:', newValue);
          
          // Hide warning when decreasing
          this.hideStockWarning();
          
          // Update button states
          this.updateQuantityButtonsState();

          // Dispatch change event
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      getActualStockQuantity() {
        console.log('🔄 Getting actual stock quantity from screen...');
        
        // Method 1: Look for .product-stock element with "Available stock: X" format
        const productStockElement = this.querySelector('.product-stock');
        if (productStockElement) {
          const stockText = productStockElement.textContent || productStockElement.innerText;
          console.log('📊 Product stock element text:', stockText);
          
          // Extract number from "Available stock: X" format
          if (stockText.includes('Available stock:')) {
            const stockMatch = stockText.match(/Available stock:\s*(\d+)/i);
            if (stockMatch && stockMatch[1]) {
              const stock = parseInt(stockMatch[1]);
              console.log('✅ Stock extracted from product-stock element:', stock);
              return stock;
            }
          }
          
          // Check for "Out of Stock" text
          if (stockText.includes('Out of Stock')) {
            console.log('❌ Product marked as out of stock');
            return 0;
          }
        }
        
        // Method 2: Get from current variant object
        if (this.currentVariant && typeof this.currentVariant.inventory_quantity === 'number') {
          const stock = this.currentVariant.inventory_quantity;
          console.log('📊 Stock from currentVariant:', stock);
          return stock;
        }
        
        // Method 3: Get from variant-selects data attribute
        const variantSelects = this.querySelector('variant-selects');
        if (variantSelects) {
          const selectedVariantElement = variantSelects.querySelector('[data-selected-variant]');
          if (selectedVariantElement) {
            try {
              const variantData = JSON.parse(selectedVariantElement.innerHTML);
              if (variantData && typeof variantData.inventory_quantity === 'number') {
                console.log('✅ Stock from variant-selects:', variantData.inventory_quantity);
                return variantData.inventory_quantity;
              }
            } catch (e) {
              console.log('❌ Error parsing variant data from variant-selects');
            }
          }
        }
        
        // Method 4: Check inventory element
        const inventoryElement = document.getElementById(`Inventory-${this.dataset.section}`);
        if (inventoryElement && !inventoryElement.classList.contains('hidden')) {
          const inventoryText = inventoryElement.textContent || inventoryElement.innerText;
          console.log('📊 Inventory element text:', inventoryText);
          
          // Try to extract number from inventory text
          const stockMatch = inventoryText.match(/(\d+)/);
          if (stockMatch) {
            const stock = parseInt(stockMatch[1]);
            console.log('✅ Stock extracted from inventory display:', stock);
            return stock;
          }
        }
        
        // Method 5: Check quantity input max attribute
        if (this.quantityInput) {
          const maxAttr = this.quantityInput.getAttribute('max');
          if (maxAttr && maxAttr !== 'Infinity' && maxAttr !== '0') {
            const stock = parseInt(maxAttr);
            console.log('📊 Stock from max attribute:', stock);
            return isNaN(stock) ? 0 : stock;
          }
        }
        
        console.log('⚠️ No stock quantity found, checking product availability...');
        
        // Method 6: Check if product is marked as sold out
        const addButton = this.querySelector('[name="add"]');
        if (addButton && addButton.disabled) {
          const buttonText = addButton.textContent || addButton.innerText;
          if (buttonText.toLowerCase().includes('sold out') || buttonText.toLowerCase().includes('unavailable')) {
            console.log('❌ Product marked as sold out');
            return 0;
          }
        }
        
        // Method 7: Check if .product-stock element exists but no stock found (likely out of stock)
        if (productStockElement && productStockElement.textContent.includes('Out of Stock')) {
          console.log('❌ Product marked as out of stock in product-stock element');
          return 0;
        }
        
        console.log('❌ No stock information found, defaulting to 0');
        return 0;
      }

      updateQuantityButtonsState() {
        const buttons = this.quantityButtons;
        if (!buttons.plus || !buttons.minus || !this.quantityInput) return;

        const maxQty = this.getActualStockQuantity();
        const minQty = parseInt(this.quantityInput.getAttribute('min')) || 1;
        const currentValue = parseInt(this.quantityInput.value) || 1;

        console.log('🔄 Updating Buttons - Current:', currentValue, 'Max:', maxQty, 'Min:', minQty);

        // Plus button - disable if at or exceeding max stock OR out of stock
        const shouldDisablePlus = currentValue >= maxQty || maxQty <= 0;
        
        if (shouldDisablePlus) {
          buttons.plus.disabled = true;
          buttons.plus.setAttribute('disabled', 'disabled');
          buttons.plus.setAttribute('aria-disabled', 'true');
          buttons.plus.classList.add('quantity__button--disabled');
          buttons.plus.style.opacity = '0.5';
          buttons.plus.style.cursor = 'not-allowed';
          buttons.plus.title = maxQty <= 0 ? 'Out of stock' : `Maximum ${maxQty} available`;
          console.log('🔴 Plus button DISABLED - Reached max stock');
        } else {
          buttons.plus.disabled = false;
          buttons.plus.removeAttribute('disabled');
          buttons.plus.removeAttribute('aria-disabled');
          buttons.plus.classList.remove('quantity__button--disabled');
          buttons.plus.style.opacity = '1';
          buttons.plus.style.cursor = 'pointer';
          buttons.plus.title = 'Increase quantity';
          console.log('🟢 Plus button ENABLED - Can increase');
        }

        // Minus button - disable only if at minimum
        const shouldDisableMinus = currentValue <= minQty;
        if (shouldDisableMinus) {
          buttons.minus.disabled = true;
          buttons.minus.setAttribute('disabled', 'disabled');
          buttons.minus.setAttribute('aria-disabled', 'true');
          buttons.minus.classList.add('quantity__button--disabled');
          buttons.minus.style.opacity = '0.5';
          buttons.minus.style.cursor = 'not-allowed';
          buttons.minus.title = 'Minimum quantity reached';
        } else {
          buttons.minus.disabled = false;
          buttons.minus.removeAttribute('disabled');
          buttons.minus.removeAttribute('aria-disabled');
          buttons.minus.classList.remove('quantity__button--disabled');
          buttons.minus.style.opacity = '1';
          buttons.minus.style.cursor = 'pointer';
          buttons.minus.title = 'Decrease quantity';
        }
      }

      validateQuantityInput() {
        if (!this.quantityInput) return;
        
        const max = this.getActualStockQuantity();
        const min = parseInt(this.quantityInput.getAttribute('min')) || 1;
        const previousValue = parseInt(this.quantityInput.getAttribute('data-previous-value')) || min;
        let val = parseInt(this.quantityInput.value);
        
        console.log('✓ Validating Input - Value:', val, 'Max:', max, 'Min:', min);
        
        // Handle invalid input
        if (isNaN(val) || val <= 0) {
          this.quantityInput.value = previousValue;
          this.updateQuantityButtonsState();
          return;
        }
        
        // Handle out of stock
        if (max <= 0) {
          this.quantityInput.value = previousValue;
          this.showStockWarning(0, 'out-of-stock');
          this.updateQuantityButtonsState();
          return;
        }
        
        // Enforce maximum
        if (val > max) {
          this.quantityInput.value = max;
          this.quantityInput.setAttribute('data-previous-value', max);
          this.showStockWarning(max, 'exceeds-limit');
          this.updateQuantityButtonsState();
          return;
        }
        
        // Enforce minimum
        if (val < min) {
          this.quantityInput.value = min;
          this.quantityInput.setAttribute('data-previous-value', min);
          this.hideStockWarning();
          this.updateQuantityButtonsState();
          return;
        }
        
        // Valid quantity
        this.quantityInput.value = val;
        this.quantityInput.setAttribute('data-previous-value', val);
        this.hideStockWarning();
        this.updateQuantityButtonsState();
      }

      validateQuantityChange() {
        if (!this.quantityInput) return;
        
        const max = this.getActualStockQuantity();
        const min = parseInt(this.quantityInput.getAttribute('min')) || 1;
        const previousValue = parseInt(this.quantityInput.getAttribute('data-previous-value')) || min;
        let val = parseInt(this.quantityInput.value);
        
        console.log('✓ Validating Change - Value:', val, 'Max:', max);
        
        // Handle invalid input
        if (isNaN(val) || val <= 0) {
          this.quantityInput.value = previousValue;
          this.updateQuantityButtonsState();
          return;
        }
        
        // Handle out of stock
        if (max <= 0) {
          this.quantityInput.value = previousValue;
          this.showStockWarning(0, 'out-of-stock');
          this.updateQuantityButtonsState();
          return;
        }
        
        // Clamp to max if exceeded
        if (val > max) {
          this.quantityInput.value = max;
          this.quantityInput.setAttribute('data-previous-value', max);
          this.showStockWarning(max, 'exceeds-limit');
        } else if (val < min) {
          this.quantityInput.value = min;
          this.quantityInput.setAttribute('data-previous-value', min);
          this.hideStockWarning();
        } else if (val === max) {
          this.quantityInput.setAttribute('data-previous-value', val);
          this.showStockWarning(max, 'at-limit');
        } else {
          this.quantityInput.setAttribute('data-previous-value', val);
          this.hideStockWarning();
        }
        
        this.updateQuantityButtonsState();
      }

      showStockWarning(maxStock, type = 'exceeds-limit') {
        console.log('⚠️ Showing warning - Stock:', maxStock, 'Type:', type);
        
        if (this.warningTimeout) {
          clearTimeout(this.warningTimeout);
        }
        
        this.hideStockWarning();
        
        // Determine message
        // let message = '';
        // let icon = '⚠️';
        // let bgColor = '#fef3c7';
        // let textColor = '#d97706';
        // let borderColor = '#f59e0b';
        
        // switch(type) {
        //   case 'out-of-stock':
        //     message = 'This item is currently out of stock.';
        //     icon = '❌';
        //     bgColor = '#fee2e2';
        //     textColor = '#dc2626';
        //     borderColor = '#dc2626';
        //     break;
        //   case 'at-limit':
        //     message = `Maximum stock reached. Only ${maxStock} ${maxStock === 1 ? 'item' : 'items'} available.`;
        //     icon = 'ℹ️';
        //     bgColor = '#dbeafe';
        //     textColor = '#1e40af';
        //     borderColor = '#3b82f6';
        //     break;
        //   case 'exceeds-limit':
        //     message = `Quantity exceeds available stock. Only ${maxStock} ${maxStock === 1 ? 'item' : 'items'} in stock.`;
        //     icon = '⚠️';
        //     break;
        // }
        
        const warning = document.createElement('div');
        warning.className = 'product-form__error-message-wrapper stock-warning-message';
        warning.setAttribute('data-stock-warning', type);
        warning.setAttribute('role', 'alert');
        warning.setAttribute('aria-live', 'polite');
        
        warning.style.cssText = `
          color: ${textColor};
          font-size: 14px;
          font-weight: 500;
          margin: 12px 0;
          padding: 12px 16px;
          background-color: ${bgColor};
          border-radius: 6px;
          border-left: 4px solid ${borderColor};
          display: flex;
          align-items: center;
          gap: 8px;
          animation: slideIn 0.3s ease-out;
        `;
        
        warning.innerHTML = `<span style="font-size: 16px;">${icon}</span><span>${message}</span>`;
        
        // Add animation
        if (!document.getElementById('stock-warning-animation')) {
          const style = document.createElement('style');
          style.id = 'stock-warning-animation';
          style.textContent = `
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `;
          document.head.appendChild(style);
        }
        
        // Place warning
        const addButton = this.querySelector('[name="add"]');
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        const quantityContainer = this.quantityInput?.closest('.quantity');
        
        if (addButton && addButton.parentNode) {
          addButton.parentNode.insertBefore(warning, addButton);
        } else if (quantityContainer) {
          quantityContainer.appendChild(warning);
        } else if (productForm) {
          productForm.insertBefore(warning, productForm.firstChild);
        }
        
        // Auto-hide after 5 seconds (except out-of-stock)
        if (type !== 'out-of-stock') {
          this.warningTimeout = setTimeout(() => {
            this.hideStockWarning();
          }, 5000);
        }
      }

      hideStockWarning() {
        const warnings = this.querySelectorAll('[data-stock-warning]');
        warnings.forEach(warning => warning.remove());
        
        if (this.warningTimeout) {
          clearTimeout(this.warningTimeout);
          this.warningTimeout = null;
        }
      }

      setQuantityBoundaries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      setQuantityMax(variant) {
        if (!this.quantityInput) {
          console.log('❌ No quantity input found');
          return;
        }
        
        // Update current variant first
        this.currentVariant = variant;
        
        // Get stock from screen (most reliable)
        const maxQty = this.getActualStockQuantity();
        const currentValue = parseInt(this.quantityInput.value) || 1;

        console.log('📦 Setting Max Stock:', maxQty, 'Current Value:', currentValue, 'Variant ID:', variant?.id);
        
        if (maxQty > 0) {
          // Set both attributes
          this.quantityInput.setAttribute('max', maxQty.toString());
          this.quantityInput.setAttribute('data-stock-quantity', maxQty.toString());
          
          console.log('✅ Set max to:', maxQty);
          
          // Clamp if needed
          if (currentValue > maxQty) {
            this.quantityInput.value = maxQty;
            this.quantityInput.setAttribute('data-previous-value', maxQty.toString());
            this.showStockWarning(maxQty, 'exceeds-limit');
          } else if (currentValue < 1) {
            this.quantityInput.value = '1';
            this.quantityInput.setAttribute('data-previous-value', '1');
          } else {
            this.quantityInput.setAttribute('data-previous-value', currentValue.toString());
          }
          
          this.quantityInput.disabled = false;
          this.toggleAddButton(false);
          this.updateInventoryDisplay(maxQty);
        } else {
          console.log('❌ Out of stock or unavailable');
          // Out of stock
          this.quantityInput.setAttribute('max', '0');
          this.quantityInput.setAttribute('data-stock-quantity', '0');
          this.quantityInput.value = '0';
          this.quantityInput.disabled = true;
          this.toggleAddButton(true, FoxTheme.variantStrings.soldOut);
          this.updateInventoryDisplay(0);
          this.showStockWarning(0, 'out-of-stock');
        }
        
        this.updateQuantityButtonsState();
      }

      updateInventoryDisplay(stockQuantity) {
        const inventoryElement = document.getElementById(`Inventory-${this.dataset.section}`);
        if (inventoryElement) {
          if (stockQuantity > 0) {
            inventoryElement.classList.remove('hidden');
          } else {
            inventoryElement.classList.add('hidden');
          }
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      resetProductFormState() {
        this.productForm?.resetFormState();
        this.hideStockWarning();
        // Reset quantity to 1 when variant changes
        if (this.quantityInput) {
          this.quantityInput.value = '1';
          this.quantityInput.setAttribute('data-previous-value', '1');
        }
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);
          this.currentVariant = variant;
          this.setQuantityMax(variant);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          
          const variant = this.getSelectedVariant(html);
          
          console.log('🔄 Updating variant to:', variant?.id, 'Stock:', variant?.inventory_quantity);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);
          
          // Update current variant FIRST
          this.currentVariant = variant;
          this.setQuantityMax(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : !variant?.available,
            variant?.available ? FoxTheme.variantStrings.addToCart : FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        const variant = selectedVariant ? JSON.parse(selectedVariant) : null;
        
        if (variant) {
          console.log('📋 Variant Selected:', variant.id);
          console.log('📦 Stock Quantity:', variant.inventory_quantity);
          console.log('✓ Available:', variant.available);
        } else {
          console.log('❌ No variant found in productInfoNode');
        }
        
        return variant;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Product info fetch error:', error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return;

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true;
          }
          return false;
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = text || FoxTheme.variantStrings.addToCart;
        }
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        
        if (this.quantityInput) {
          this.quantityInput.disabled = true;
          this.quantityInput.setAttribute('max', '0');
          this.quantityInput.value = '0';
        }
        
        this.updateQuantityButtonsState();
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.querySelector('input[name="id"]')?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error('Quantity rules fetch error:', error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundaries();
      }

      validateQuantityBeforeSubmit() {
        if (!this.quantityInput) return true;
        
        const maxQty = this.getActualStockQuantity();
        const currentQty = parseInt(this.quantityInput.value) || 1;
        
        console.log('✓ Pre-submit validation - Current:', currentQty, 'Max:', maxQty);
        
        if (maxQty <= 0) {
          this.showStockWarning(0, 'out-of-stock');
          return false;
        }
        
        if (currentQty > maxQty) {
          this.quantityInput.value = maxQty;
          this.showStockWarning(maxQty, 'exceeds-limit');
          return false;
        }
        
        return true;
      }

      getStockStatus() {
        if (!this.quantityInput) return { available: false, quantity: 0 };
        
        const maxQty = this.getActualStockQuantity();
        const currentQty = parseInt(this.quantityInput.value) || 0;
        
        return {
          available: maxQty > 0,
          maxQuantity: maxQty,
          currentQuantity: currentQty,
          canPurchase: currentQty > 0 && currentQty <= maxQty,
          isAtLimit: currentQty >= maxQty,
          isOutOfStock: maxQty <= 0
        };
      }
    }
  );
}